import { type FC } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { Header } from 'dibs-elements/exports/Header.tsx';
import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { NftProfileSectionLoadMore } from './NftProfileSectionLoadMore.tsx';
import { trackPdpImpression, trackPdpRedirect } from './nftCollectorTracking.ts';

import styles from './NftProfileSections.scss';

import { type NftProfileSectionLiveBids_viewer$key as ViewerRef } from './__generated__/NftProfileSectionLiveBids_viewer.graphql.ts';
import { type NftProfileSectionLiveBidsPaginationQuery as PaginationQuery } from './__generated__/NftProfileSectionLiveBidsPaginationQuery.graphql.ts';

type Props = {
    isMobile: boolean;
    viewer: ViewerRef;
};

export const NftProfileSectionLiveBids: FC<Props> = ({ isMobile, viewer: viewerRef }) => {
    const { currency } = useCurrency();
    const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
        PaginationQuery,
        ViewerRef
    >(
        graphql`
            fragment NftProfileSectionLiveBids_viewer on Viewer
            @refetchable(queryName: "NftProfileSectionLiveBidsPaginationQuery") {
                bidderLiveBids: nftAuctionsByBidder(
                    username: $username
                    bidderAddress: $walletAddress
                    first: $liveBidsFirst
                    after: $liveBidsCursor
                    status: BIDDER_LIVE
                ) @connection(key: "NftProfileSectionLiveBids_bidderLiveBids") {
                    edges {
                        node {
                            ...NftItemTile_nftAuctionBidderRef
                            auction {
                                ...NftItemTile_nftAuction
                                item {
                                    serviceId
                                    metadata {
                                        ... on NftDigitalArtMetadata {
                                            name
                                        }
                                    }
                                    ...NftItemTile_nftItem
                                }
                            }
                        }
                    }
                }
                ...NftHeartPillProvider_viewer
                ...NftItemTile_viewer
            }
        `,
        viewerRef
    );

    return (
        <div className={styles.auctionSectionContainer}>
            <div className={styles.auctionSectionHeader}>
                <Header
                    title={
                        <FormattedMessage
                            id="nftProfile.auctionSection.liveBids.title"
                            defaultMessage="Live Auctions"
                        />
                    }
                    htmlElementType="h2"
                />
            </div>
            <NftHeartPillProvider
                viewer={data}
                nftItemIds={(data.bidderLiveBids?.edges || [])
                    .map(edge => edge?.node?.auction?.item?.serviceId || null)
                    .filter(filterNulls)}
            >
                <ul className={styles.auctionItems}>
                    {data.bidderLiveBids?.edges?.map((edge, index) => {
                        const auctionBidderRef = edge?.node;
                        const nftAuction = auctionBidderRef?.auction;
                        const nftItem = nftAuction?.item;

                        if (!auctionBidderRef || !nftItem) {
                            return null;
                        }

                        return (
                            <li className={styles.itemTileWrapper} key={index}>
                                <NftItemTile
                                    tileType="COLLECTOR_TILE"
                                    nftItem={nftItem}
                                    nftAuction={nftAuction}
                                    nftAuctionBidderRef={auctionBidderRef}
                                    fireTracking={trackPdpRedirect(
                                        nftItem.serviceId,
                                        nftItem.metadata?.name,
                                        index,
                                        'BIDS_TAB'
                                    )}
                                    fireImpressionTracking={trackPdpImpression(
                                        nftItem.serviceId,
                                        nftItem.metadata?.name,
                                        index,
                                        'BIDS_TAB'
                                    )}
                                    viewer={data}
                                    currency={currency}
                                    gaLabel="NFT-Collector"
                                    isMobile={isMobile}
                                />
                            </li>
                        );
                    })}
                </ul>
            </NftHeartPillProvider>

            <NftProfileSectionLoadMore
                hasNext={hasNext}
                isLoadingNext={isLoadingNext}
                onClick={() => loadNext(20)}
            />
        </div>
    );
};
